

export enum FieldType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATE = 'date',
  ENUM = 'enum',
  DIALOG = 'dialog',
  PARAMETER = 'parameter',
}

export enum InputControl {
  TEXT = 'text',
  TEXTAREA = 'textarea',
  SELECT = 'select',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  DATEPICKER = 'datepicker',
  NUMBER = 'number',
  BUTTON = 'button',
  READONLY = 'readonly',
}

export interface FieldValidation {
  required?: boolean;
  min?: number;
  max?: number;
  pattern?: string;
  customMessage?: string;
}

export interface DataSourceItem {
  key: string;
  value: string;
}

export enum ModifierType {
  HIDDEN = 'hidden',
  REQUIRED = 'required',
  DISABLED = 'disabled',
}

export enum ConditionOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  IS_TRUE = 'is_true',
  IS_FALSE = 'is_false',
  IS_EMPTY = 'is_empty',
  IS_NOT_EMPTY = 'is_not_empty',
}

export interface Condition {
  id: string;
  sourceFieldId: string;
  operator: ConditionOperator;
  value?: any;
}

export interface Modifier {
  id: string;
  type: ModifierType;
  matchType: 'all' | 'any';
  conditions: Condition[];
}

export interface Group {
  id: string;
  label: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export interface ParameterConfiguration {
    routeKey: string;
    allowedEntities: string; // Keep for backward compatibility, but UI may prefer datasourceString
}

export interface Field {
  id: string;
  type: FieldType;
  label: string;
  inputControl: InputControl;
  validation?: FieldValidation;
  defaultValue?: any;
  placeholder?: string;
  dataSource?: DataSourceItem[]; // For static options
  datasourceString?: string; // For dynamic datasource config (Query::Filter::Model)
  groupId?: string;
  modifiers?: Modifier[];
  parameterConfig?: ParameterConfiguration;
  // New properties for external schema compatibility
  disabled?: boolean;
  hidden?: boolean;
  extensions?: Record<string, string>;
}

export interface FormDefinition {
  id: string;
  name: string;
  fields: Field[];
  groups: Group[];
  version: string;
  modifiersScript?: string; // For "Modifiers": "<Late-Bound Eval Script or Static>"
}

export interface EditorState {
  form: FormDefinition;
  selectedElementId: string | null;
  selectedElementType: 'field' | 'group' | null;
  isPreviewMode: boolean;
  history: FormDefinition[];
  historyIndex: number;
}

export type EditorAction =
  | { type: 'SET_FORM_NAME'; payload: string }
  | { type: 'SET_FORM_MODIFIERS_SCRIPT'; payload: string }
  | { type: 'ADD_FIELD'; payload: { field: Field; groupId?: string; index?: number } }
  | { type: 'UPDATE_FIELD'; payload: { id: string; updates: Partial<Field> } }
  | { type: 'DELETE_FIELD'; payload: string }
  | { type: 'DUPLICATE_FIELD'; payload: string }
  | { type: 'SELECT_ELEMENT'; payload: { id: string | null; type: 'field' | 'group' | null } }
  | { type: 'MOVE_FIELD'; payload: { fieldId: string; targetGroupId: string | undefined; newIndex: number } }
  | { type: 'ADD_GROUP'; payload: Group }
  | { type: 'UPDATE_GROUP'; payload: { id: string; updates: Partial<Group> } }
  | { type: 'DELETE_GROUP'; payload: string }
  | { type: 'REORDER_GROUPS'; payload: { activeId: string; overId: string } }
  | { type: 'TOGGLE_PREVIEW'; payload: boolean }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'LOAD_FORM'; payload: FormDefinition };
